/**
 * PURPOSE: Lista de disciplinas del Cómputo General (FTR-0066) para elegir cuál editar.
 * RESPONSIBILITIES: Buscador sin tildes (nombre o sigla), grupos "Colectivas" / "Individuales", puestos cargados e
 *   inscritas por disciplina, aviso de participaciones desmarcadas y disciplinas sin inscritas deshabilitadas. En
 *   pantallas angostas va arriba con altura limitada; en anchas, a la izquierda.
 * COLLABORATORS: ComputoGeneralArea.jsx, computoGeneral.utils.js.
 * ROUTE: winscore/src/area/ComputoGeneralArea/ComputoDisciplineList.jsx
 */
import React, { useMemo, useState } from "react";
import { FiSearch } from "react-icons/fi";

import { filterDisciplines, formatDisciplineCounts, groupDisciplines } from "./computoGeneral.utils.js";

const ComputoDisciplineList = ({ disciplines = [], selectedSportId, onSelect, disabled = false }) => {
  const [query, setQuery] = useState("");
  const groups = useMemo(() => groupDisciplines(filterDisciplines(disciplines, query)), [disciplines, query]);

  return (
    <aside className="flex min-w-0 flex-col rounded-2xl border border-gray-100 bg-white p-3 shadow-sm" aria-label="Disciplinas">
      <label className="relative block">
        <FiSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Buscar disciplina o sigla..."
          aria-label="Buscar disciplina"
          className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-9 pr-3 text-sm font-semibold text-slate-800 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
        />
      </label>

      <div className="mt-3 max-h-72 overflow-y-auto pr-1 lg:max-h-[70vh]">
        {groups.length === 0 ? (
          <p className="px-2 py-6 text-center text-sm font-semibold text-slate-500">No hay disciplinas con esa búsqueda.</p>
        ) : null}

        {groups.map((group) => (
          <div key={group.key} className="mb-3 last:mb-0">
            <p className="px-2 pb-1 text-[11px] font-black uppercase tracking-[0.2em] text-slate-400">{group.label}</p>
            <ul className="grid gap-1">
              {group.items.map((discipline) => {
                const empty = Number(discipline.participants) === 0;
                const selected = String(discipline.idsport) === String(selectedSportId);
                const removed = Number(discipline.participationRemoved) || 0;
                return (
                  <li key={discipline.idsport}>
                    <button
                      type="button"
                      onClick={() => onSelect(discipline)}
                      disabled={disabled || empty}
                      aria-current={selected ? "true" : undefined}
                      title={empty ? "Sin instituciones inscritas" : discipline.label}
                      className={[
                        "flex w-full items-center gap-2 rounded-xl border px-2.5 py-2 text-left transition",
                        selected ? "border-blue-500 bg-blue-50 ring-2 ring-blue-100" : "border-transparent hover:bg-slate-50",
                        empty ? "cursor-not-allowed opacity-50" : "",
                        disabled && !empty ? "cursor-wait" : "",
                      ].join(" ")}
                    >
                      <span
                        className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-lg"
                        style={{ backgroundColor: discipline.color || "#e2e8f0" }}
                      >
                        {discipline.image ? <img src={discipline.image} alt="" className="h-6 w-6 object-contain" /> : null}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-bold text-slate-800">
                          {discipline.label}
                          {discipline.acronym ? <span className="ml-1 text-xs font-semibold text-slate-400">{discipline.acronym}</span> : null}
                        </span>
                        <span className="block text-xs font-semibold text-slate-500">
                          {empty ? "Sin instituciones inscritas" : formatDisciplineCounts(discipline)}
                        </span>
                      </span>
                      {removed > 0 ? (
                        <span
                          className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-black text-amber-800"
                          title={`${removed} institución(es) sin participación (+0)`}
                        >
                          {removed} sin part.
                        </span>
                      ) : null}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
    </aside>
  );
};

export default ComputoDisciplineList;
